// readinessCache.js — one-entry-per-day cache for morning readiness.
//
// localStorage["hayahReadinessToday"] = {
//   date,             // YYYY-MM-DD
//   classification,   // 'HARD' | 'MEDIUM' | 'EASY' | 'REST'
//   rationale,        // string (2 sentences)
//   nutritionAdvice,  // string (2 sentences)
//   keyDrivers,       // string[]
//   generatedAt,      // ms
//   steveToday,       // boolean
// }
//
// stale if date !== today — caller should regenerate.

const KEY      = "hayahReadinessToday";
const HIST_KEY = "hayahReadinessHistory"; // [{ date, classification, generatedAt }, ...], last 14 days

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function loadReadinessCache() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw || typeof raw !== "object") return null;
    if (raw.date !== localDateStr()) return null;
    return raw;
  } catch { return null; }
}

// Loads the rolling 14-day history (used as a self-feedback signal for the
// next readiness call — the model can see what it recommended yesterday).
export function loadReadinessHistory() {
  try {
    const raw = JSON.parse(localStorage.getItem(HIST_KEY) || "null");
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}

export function saveReadinessCache(entry) {
  try {
    const today = localDateStr();
    const data  = { ...entry, date: today, generatedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(data));

    // Append/overwrite today's entry in the history array (most recent first).
    // Score is stored too so the card can draw a real readiness trend and grade
    // past predictions against what actually happened. Any feel the user already
    // logged today is carried over so a recompute doesn't wipe it.
    const existingToday = loadReadinessHistory().find(e => e.date === today);
    const hist = loadReadinessHistory().filter(e => e.date !== today);
    hist.unshift({
      date: today,
      classification: data.classification,
      score: data.score ?? null,
      scoreBand: data.scoreBand ?? null,
      generatedAt: data.generatedAt,
      feel: existingToday?.feel ?? null,
      feelCtx: existingToday?.feelCtx ?? null,
    });
    while (hist.length > 30) hist.pop();
    try { localStorage.setItem(HIST_KEY, JSON.stringify(hist)); } catch {}

    try { window.dispatchEvent(new StorageEvent("storage", { key: KEY })); } catch {}
    return data;
  } catch { return null; }
}

export function clearReadinessCache() {
  try { localStorage.removeItem(KEY); } catch {}
}

// Record the user's subjective feel (and a context snapshot) onto today's
// history entry, so the objective-vs-subjective gap can be learned over time.
// Creates today's entry if a readiness hasn't been generated yet.
export function recordFeel(feel, ctx) {
  try {
    const today = localDateStr();
    const hist = loadReadinessHistory();
    const idx = hist.findIndex(e => e.date === today);
    if (idx >= 0) {
      hist[idx] = { ...hist[idx], feel: feel || null, feelCtx: feel ? (ctx || hist[idx].feelCtx || null) : (hist[idx].feelCtx || null) };
    } else {
      hist.unshift({ date: today, classification: null, score: ctx?.objScore ?? null, scoreBand: ctx?.objBand ?? null, generatedAt: Date.now(), feel: feel || null, feelCtx: feel ? (ctx || null) : null });
      while (hist.length > 30) hist.pop();
    }
    localStorage.setItem(HIST_KEY, JSON.stringify(hist));
  } catch {}
}
